// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userProgress, setUserProgress] = useState({
    level: 1,
    total_points: 0,
    streak_days: 0,
    completed_lessons: 0,
    badges: []
  });
  const [isLoading, setIsLoading] = useState(true);

  // Verificar token ao carregar
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      loadUserProfile();
    } else {
      setIsLoading(false);
    }
  }, []);

  // Carregar perfil do usuário
  const loadUserProfile = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getUserProfile();
      
      setCurrentUser(response.user);
      setUserProgress(response.progress);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Erro ao carregar perfil:', error);
      localStorage.removeItem('token');
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Login
  const login = async (credentials) => {
    try {
      const response = await apiService.login(credentials);
      
      localStorage.setItem('token', response.token);
      setCurrentUser(response.user);
      setIsAuthenticated(true);
      
      // Carregar progresso após login
      await loadUserProfile();
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Registro
  const register = async (userData) => {
    try {
      const response = await apiService.register(userData);
      
      localStorage.setItem('token', response.token);
      setCurrentUser(response.user);
      setIsAuthenticated(true);
      
      // Carregar progresso após registro
      await loadUserProfile();
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Logout
  const logout = () => {
    localStorage.removeItem('token');
    setCurrentUser(null);
    setIsAuthenticated(false);
    setUserProgress({
      level: 1,
      total_points: 0,
      streak_days: 0,
      completed_lessons: 0,
      badges: []
    });
  };

  // Atualizar progresso
  const updateProgress = async (pointsEarned = 0, lessonCompleted = false) => {
    try {
      const response = await apiService.updateProgress({
        points_earned: pointsEarned,
        lesson_completed: lessonCompleted
      });

      setUserProgress(prev => ({
        ...prev,
        ...response.progress
      }));

      return { success: true };
    } catch (error) {
      console.error('Erro ao atualizar progresso:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    isAuthenticated,
    currentUser,
    userProgress,
    isLoading,
    login,
    register,
    logout,
    updateProgress,
    loadUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};